import { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { X, BookMarked, BookOpen, Brain, Briefcase, Target, Lightbulb, FlaskConical, Heart, Globe, GraduationCap, Coins, MessageCircle, Feather } from 'lucide-react';
import { cn } from '@/utils/cn';

import { useDebounceValue } from 'usehooks-ts';
import { useSearchBooks } from '../hooks/useSearchBooks';
import { useRecommendedBooksInfinite } from '../hooks/useRecommendedBooksInfinite';
import BookCard from '../components/BookCard';
import { useTopics } from '@/features/learning/hooks/useTopics';
import searchIconUrl from './Search.svg?url';
import HeroPromoBanner from '../components/HeroPromoBanner';
import LearningPackageRow from '../components/LearningPackageRow';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

const LibraryPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 500);
  const { topics } = useTopics();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: subStatus, isLoading: subLoading } = useSubscriptionStatus();

  const { 
    books: recommendedBooks, 
    isLoading: recommendedLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage
  } = useRecommendedBooksInfinite({
    enabled: true,
    limit: 10,
  });

  const { data: searchData, isLoading: searchLoading } = useSearchBooks({
    title: debouncedSearchTerm,
    page: 1,
    limit: 20,
  }, {
    enabled: debouncedSearchTerm.length > 0,
  });

  const isSearching = debouncedSearchTerm.length > 0;
  const books = isSearching
    ? (searchData?.books ?? [])
    : recommendedBooks;
  const isLoading = isSearching ? searchLoading : recommendedLoading;
  const isSubscriptionActive = Boolean(subStatus?.is_active);

  const carouselBooks = books.map((b) => ({
    book_id: b.book_id,
    title: b.title,
    author: b.author,
    cover_image_url: b.cover_image_url ?? null,
    topic: b.topic,
  }));

  // Handle horizontal scroll for infinite loading (RTL aware)
  const handleScroll = useCallback(() => {
    if (isSearching) return; // Don't infinite scroll for search results
    
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    // In RTL, scrollLeft is 0 at start (right side) and becomes negative as you scroll left
    const maxScroll = scrollWidth - clientWidth;
    const currentScroll = Math.abs(scrollLeft);
    
    // Load more when scrolled to 80% of the content (near the left end in RTL)
    if (currentScroll >= maxScroll * 0.8) {
      fetchNextPage();
    }
  }, [isSearching, isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const iconForTopic = (name: string) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('روان') || n.includes('psych')) return <Brain className="w-4 h-4" />;
    if (n.includes('کسب') || n.includes('بیز') || n.includes('business') || n.includes('مدیر') || n.includes('رهبری')) return <Briefcase className="w-4 h-4" />;
    if (n.includes('بازاری') || n.includes('مارکت') || n.includes('فروش') || n.includes('هدف')) return <Target className="w-4 h-4" />;
    if (n.includes('ایده') || n.includes('خلاق') || n.includes('نوآوری') || n.includes('innovation')) return <Lightbulb className="w-4 h-4" />;
    if (n.includes('علم') || n.includes('شیمی') || n.includes('فیزیک') || n.includes('science')) return <FlaskConical className="w-4 h-4" />;
    if (n.includes('سلامت') || n.includes('خانواده') || n.includes('روابط') || n.includes('health')) return <Heart className="w-4 h-4" />;
    if (n.includes('جهان') || n.includes('تاریخ') || n.includes('بین') || n.includes('world')) return <Globe className="w-4 h-4" />;
    if (n.includes('یادگیری') || n.includes('آموزش') || n.includes('تحصیل') || n.includes('education')) return <GraduationCap className="w-4 h-4" />;
    if (n.includes('مالی') || n.includes('اقتصاد') || n.includes('finance') || n.includes('money')) return <Coins className="w-4 h-4" />;
    if (n.includes('ارتباط') || n.includes('communication')) return <MessageCircle className="w-4 h-4" />;
    if (n.includes('داستان') || n.includes('رمان') || n.includes('fiction') || n.includes('novel')) return <Feather className="w-4 h-4" />;
    return <BookOpen className="w-4 h-4" />;
  };

  return (
    <div className={cn('min-h-screen bg-white')} dir="rtl">
      <div className="pb-28">
        {/* Simple Static Header */}
        <header className="px-4 bg-white sticky top-0 z-40 pt-8 pb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 h-12 bg-gray-100 dark:bg-dark-bg-secondary rounded-2xl flex items-center px-4">
              <input
                ref={inputRef}
                type="text"
                placeholder="جستجو در نام کتاب ، نویسنده و ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-full bg-transparent border-0 outline-none focus:ring-0 focus:outline-none appearance-none text-sm text-gray-800 dark:text-dark-foreground placeholder:text-gray-400 dark:placeholder:text-dark-muted text-right pr-12 pl-10 mb-0"
              />
              <img src={searchIconUrl} alt="search" className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6" />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>
            <button
              onClick={() => navigate('/library/my-books')}
              className="flex-shrink-0 h-12 w-12 bg-gray-100 dark:bg-dark-bg-secondary rounded-2xl flex items-center justify-center hover:bg-gray-200 dark:hover:bg-dark-bg-tertiary transition-colors shadow-md hover:shadow-lg transition-shadow"
              aria-label="کتاب‌های من"
            >
              <BookMarked className="w-6 h-6 text-gray-700 dark:text-dark-muted" />
            </button>
          </div>
        </header>

        {/* Featured Section - Loading State or Banner */}
        {!isSearching && (subLoading || !isSubscriptionActive) && (
          <section className="px-4 py-3">
            {subLoading ? (
              <div className="w-full h-48 rounded-2xl bg-gray-200 animate-pulse" />
            ) : (
              !isSubscriptionActive && <HeroPromoBanner location="library" />
            )}
          </section>
        )}

        {/* Category Chips */}
        {!isSearching && (
           <section className="pt-1 pb-4">
            <h2 className="font-persian font-bold text-sm sm:text-base mb-4 text-right px-4">دسته بندی</h2>
            <div
              className="grid grid-cols-2 gap-3 px-4"
            >
              {topics.map((topic) => (
                <button
                  key={topic.topic_name}
                  className={cn(
                    'group min-w-0 px-2 py-3 rounded-2xl bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-foreground font-medium text-sm transition-all duration-300 shadow-sm hover:shadow hover:border-primary-200 hover:bg-primary-50/40 dark:hover:bg-dark-bg-tertiary'
                  )}
                  onClick={() => navigate(`/library/category/${encodeURIComponent(topic.topic_name)}`)}
                  aria-label={String(topic.topic_name)}
                >
                  <span className="flex items-center justify-center gap-2 min-w-0">
                    <span className="w-7 h-7 rounded-xl bg-gray-100 dark:bg-dark-bg-tertiary text-gray-600 dark:text-dark-muted flex items-center justify-center group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors">
                      {iconForTopic(topic.topic_name)}
                    </span>
                    <span className="font-persian truncate">{topic.topic_name}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Recommended Books Section */}
         <section className="px-4 pt-1 pb-4">
          <h2 className="font-persian font-bold text-sm sm:text-base mb-4 text-right">پیشنهادی</h2>

          {/* Content Area */}
          <div>
            {isLoading && (
              <div
                 className={cn(
                   isSearching
                     ? 'grid grid-cols-3 gap-x-3 gap-y-6'
                     : 'grid grid-flow-col grid-rows-2 auto-cols-[7.25rem] gap-x-3 gap-y-4 overflow-x-auto scrollbar-hide pb-2'
                 )}
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                   <div key={i} className={isSearching ? 'w-full min-w-0' : 'w-[7.25rem] min-w-0'}>
                    <div className={cn(
                      'w-full bg-gray-200 rounded-xl animate-pulse mb-2',
                      isSearching ? 'aspect-[2/3]' : 'h-[170px]'
                    )}></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse mb-1 w-full"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && books.length > 0 && (
              <div
                ref={scrollContainerRef}
                 className={cn(
                   isSearching
                     ? 'grid grid-cols-3 gap-x-3 gap-y-6'
                     : 'grid grid-flow-col grid-rows-2 auto-cols-[7.25rem] gap-x-3 gap-y-4 overflow-x-auto scrollbar-hide pb-2'
                 )}
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                   ...(isSearching ? {} : { gridTemplateRows: 'repeat(2, minmax(0, 1fr))' }),
                }}
              >
                {books.map((book, index: number) => (
                  <div key={`${book.book_id}-${index}`} className={isSearching ? 'w-full min-w-0' : 'w-[7.25rem] min-w-0'}>
                    <BookCard
                      book={book}
                      titleClassName="text-xs leading-tight"
                      coverClassName={isSearching ? undefined : '!aspect-auto !h-[170px]'}
                      onClick={() =>
                        navigate(`/library/book`, {
                          state: {
                            bookId: book.book_id,
                            source: isSearching ? 'search' : 'recommended',
                            books: carouselBooks,
                            initialIndex: index,
                          },
                        })
                      }
                    />
                  </div>
                ))}
                {isFetchingNextPage && (
                   <div className={isSearching ? 'w-full min-w-0' : 'w-[7.25rem] min-w-0'}>
                    <div className={cn(
                      'w-full bg-gray-200 rounded-xl animate-pulse mb-2',
                      isSearching ? 'aspect-[2/3]' : 'h-[170px]'
                    )}></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse mb-1 w-full"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                  </div>
                )}
              </div>
            )}

            {!isLoading && books.length === 0 && !isSearching && (
              <div className="text-center py-8">
                <p className="text-muted-foreground text-sm font-persian">کتابی یافت نشد</p>
              </div>
            )}
          </div>
        </section>

        {/* Learning Packages Section */}
        {!isSearching && (
          <div>
            <LearningPackageRow />
          </div>
        )}

      </div>
    </div>
  );
};

export default LibraryPage;
